/* eslint-disable @typescript-eslint/no-explicit-any */
import Logger from '../services/logger.service'

export const updateValueForKey = (obj: any, key: any, val: any): void => {
  if (!obj || !key) {
    return
  }

  Object.keys(obj).includes(key)
    ? (obj[key] = val)
    : Object.values(obj).forEach((nestedObj) => updateValueForKey(nestedObj, key, val))
}

export const copyAllProps = (obj: any, propFrom: any, propTo: any): void => {
  if (!obj || !propFrom || !propTo) {
    return
  }
  Object.keys(obj).forEach((prop) => {
    if (typeof obj[prop] === 'object' && prop !== null && obj[prop] !== null) {
      copyAllProps(obj[prop], propFrom, propTo)
    } else if (prop === propFrom) {
      obj[propTo] = obj[propFrom]
    }
  })
}

export const moveAllProps = (obj: any, propFrom: any, propTo: any): void => {
  if (!obj || !propFrom || !propTo) {
    return
  }
  Object.keys(obj).forEach((prop) => {
    if (typeof obj[prop] === 'object' && prop !== null && obj[prop] !== null) {
      moveAllProps(obj[prop], propFrom, propTo)
    } else if (prop === propFrom) {
      obj[propTo] = obj[propFrom]
      delete obj[propFrom]
    }
  })
}

export const getOrSetValueFromNotation = (obj: any, path: string | any[], value: any = undefined): any => {
  if (typeof path === 'string') {
    return getOrSetValueFromNotation(obj, path.split('.'), value)
  } else if (path.length === 1 && value !== undefined) {
    return (obj[path[0]] = value)
  } else if (path.length === 0) {
    return obj
  } else {
    return getOrSetValueFromNotation(obj[path[0]], path.slice(1), value)
  }
}

export const getValueFromPathIgnoreIfSourceN_A = (obj: any, path: string | any[]): any => {
  if (typeof path === 'string') {
    return getValueFromPathIgnoreIfSourceN_A(obj, path.split('.'))
  } else if (path.length === 0) {
    return obj
  } else {
    if (path[0] in obj) {
      return getValueFromPathIgnoreIfSourceN_A(obj[path[0]], path.slice(1))
    } else {
      return null
    }
  }
}

export const setValueForPathWithCreate = (obj: any, path: string | any[], value: any = undefined): any => {
  if (typeof path === 'string') {
    return setValueForPathWithCreate(obj, path.split('.'), value)
  } else if (path.length === 1 && value !== undefined) {
    return (obj[path[0]] = value)
  } else {
    if (!(path[0] in obj)) {
      obj[path[0]] = {}
    }
    return setValueForPathWithCreate(obj[path[0]], path.slice(1), value)
  }
}

export const updateValueForPathWithCreate = (obj: any, path: string | any[], value: any = undefined): any => {
  if (typeof path === 'string') {
    return updateValueForPathWithCreate(obj, path.split('.'), value)
  } else if (path.length === 1 && value !== undefined) {
    if (typeof obj[path[0]] === 'object' && typeof value === 'object' && !Array.isArray(value)) {
      obj[path[0]] = { ...obj[path[0]], ...value }
    } else {
      obj[path[0]] = value
    }
  } else {
    if (!(path[0] in obj)) {
      obj[path[0]] = {}
    }
    return updateValueForPathWithCreate(obj[path[0]], path.slice(1), value)
  }
}

export const deleteElementFromPath = (obj: any, path: string | any[]): any => {
  if (typeof path === 'string') {
    return deleteElementFromPath(obj, path.split('.'))
  } else if (path.length === 1) {
    return delete obj[path[0]]
  } else if (path.length === 0) {
    return obj
  } else {
    if (path[0] in obj) {
      return deleteElementFromPath(obj[path[0]], path.slice(1))
    } else {
      return obj
    }
  }
}

export const renameElementForPath = (obj: any, path: string | any[], newElementName: any): any => {
  if (typeof path === 'string') {
    return renameElementForPath(obj, path.split('.'), newElementName)
  } else if (path.length === 1) {
    obj[newElementName] = obj[path[0]]
    delete obj[path[0]]
    return obj
  } else if (path.length === 0) {
    return obj
  } else {
    if (path[0] in obj) {
      return renameElementForPath(obj[path[0]], path.slice(1), newElementName)
    } else {
      return obj
    }
  }
}

export const copyElementToPath = (obj: any, fromPath: string, toPath: string): any => {
  const elemValue = getValueFromPathIgnoreIfSourceN_A(obj, fromPath)
  setValueForPathWithCreate(obj, toPath, elemValue)
  return obj
}

export const moveElementToPath = (obj: any, fromPath: string, toPath: string): any => {
  copyElementToPath(obj, fromPath, toPath)
  deleteElementFromPath(obj, fromPath)
}

export const getNumberOfNonNestedKeysInObject = (obj: any): number => {
  if (!obj) {
    return 0
  }

  let num = 0

  Object.values(obj).forEach((key) => {
    if (typeof key !== 'object') {
      num++
    }
  })

  return num
}

export const checkIfValuesFilled = (json: { [key: string]: any }): boolean => {
  for (const key in json) {
    if (Object.prototype.hasOwnProperty.call(json, key)) {
      const value = json[key]
      if (value === '' || value === null || value === undefined || value === false) {
        continue
      } else if (Array.isArray(value) && value.length === 0) {
        continue
      } else if (typeof value === 'object') {
        if (checkIfValuesFilled(value)) {
          return true
        }
      } else {
        return true
      }
    }
  }
  return false
}

export const sortObjectKeys = (obj: Record<string, unknown>): Record<string, unknown> => {
  const sortedKeys = Object.keys(obj).sort()
  const sortedObj: Record<string, unknown> = {}
  sortedKeys.forEach((key) => {
    sortedObj[key] = obj[key]
  })
  return sortedObj
}

export const areObjectsEqual = <T extends object>(obj1: T, obj2: T): boolean => {
  if (obj1 === obj2) return true

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return false
  }

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) return false

  return compareObjectsIfEqual(Object.fromEntries(Object.entries(obj1)), Object.fromEntries(Object.entries(obj2)))
}

export const compareObjectsIfEqual = (obj1: Record<string, unknown>, obj2: Record<string, unknown>): boolean => {
  const sortedObj1 = sortObjectKeys(obj1)
  const sortedObj2 = sortObjectKeys(obj2)
  return JSON.stringify(sortedObj1) === JSON.stringify(sortedObj2)
}

export const filterOutObjectKeys = (obj: any, keysToFilter: string[], filterType = 'filterout') => {
  switch (filterType) {
    case 'filterin':
      return Object.fromEntries(Object.entries(obj).filter(([key]) => keysToFilter.includes(key)))
    default:
      return Object.fromEntries(Object.entries(obj).filter(([key]) => !keysToFilter.includes(key)))
  }
}

/**
 * Returns the value of a key from an object
 *
 * @param key Key which may or may not exist in type `T` or an anonymous function which returns the value
 * @param instance Object instance of type `T`
 * @returns If `key` exists in `instance`:`T`, value of `instance[key]`, otherwise `null`
 */
export const getValueOrNull = <T extends { [k: string]: any }>(
  key: keyof T | ((i: T) => any) | null,
  instance: T
): any => {
  if (key instanceof Function) {
    try {
      return key(instance)
    } catch (error: unknown) {
      const { message, stackTrace } = error as any
      Logger.error(`Error produced during getValueOrNull invocation: ${message} ${stackTrace}`)
      return null
    }
  }

  if (key != null && Object.prototype.hasOwnProperty.call(instance, key)) {
    return instance[key]
  }

  return null
}

/**
 * Returns the string equivalent of object's key
 *
 * @remarks
 * You are probably thinking, "I am passing the property name as a string, why does this function exist?"
 *
 * Answer: This method allows type-safe retrieval of known properties (e.g. of an `interface`) from
 * an object which implements that `interface`. If the `string` name for a key was used directly
 * and the object no longer implemented the interface, or if the `interface` property
 * name was changed, this code would successfully build and problems would not
 * become evident until testing (hopefully) or deployment (hopefully not). Use of this type of
 * helper method will cause compile-time errors and catch problems before they occur.
 *
 * @param key Key which exists in type `T`
 * @returns String equivalent of `key`
 */
export const nameof = <T>(key: keyof T): string => {
  return key.toString()
}

/**
 * Type used for passing a reference to a property of a given object of `type T`
 */
export type GenericField<T> = keyof T | ((instance: T) => any) | null
/* eslint-enable @typescript-eslint/no-explicit-any */

export const transformAndMoveData = (
  sourceField: string,
  targetField: string,
  dataObj: any,
  transformFunction: (item: any) => any,
  filterFunction?: (item: any) => boolean,
  mapping?: Record<string, string>,
  removeMovedEntries = false
) => {
  if (dataObj[sourceField] && Array.isArray(dataObj[sourceField])) {
    dataObj[targetField] = dataObj[targetField] || {}

    const movedEntries: any[] = []

    dataObj[sourceField].forEach((item: any) => {
      if (!filterFunction || filterFunction(item)) {
        const transformedData = transformFunction(item)
        const targetKey = mapping?.[item.element]

        if (targetKey) {
          dataObj[targetField][targetKey] = dataObj[targetField][targetKey] || {}
          Object.assign(dataObj[targetField][targetKey], transformedData)

          if (removeMovedEntries) {
            movedEntries.push(item)
          }
        }
      }
    })

    if (removeMovedEntries) {
      dataObj[sourceField] = dataObj[sourceField].filter((item: any) => !movedEntries.includes(item))
    }
  }
  return dataObj
}

/**
 *
 * @param obj Any object
 * @param keys Properties expected to be in that object
 * @returns Determines whether all the members of an array are present in the object
 */
export const hasAllProperties = <T extends object>(obj: any, keys: Array<keyof T>): obj is T => {
  return keys.every((key) => key in obj)
}
